import { Clock, Globe, MessageCircle, PauseCircle, Settings, Users } from "lucide-react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SettingCard } from "@/components/settings/setting-card";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { FormActionButtons, NavigationGuardProvider } from "@/lib/components/navigation-guard";
import { PageContent } from "@/lib/components/page-content";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";
import { AdvanceBookingConfig } from "./components/advance-booking-config";
import { BankTransferToggle } from "./components/bank-transfer-toggle";
import { BookingSlotsConfig } from "./components/booking-slots-config";
import { BookingTimingConfig } from "./components/booking-timing-config";
import { OnlineBookingToggle } from "./components/online-booking-toggle";
import { PauseBookingConfig } from "./components/pause-booking-config";
import { SalonSlugConfig } from "./components/salon-slug-config";
import { ServiceSelectionModeToggle } from "./components/service-selection-mode-toggle";
import { ShowServiceDurationToggle } from "./components/show-service-duration-toggle";
import { TrafficSourcesConfig } from "./components/traffic-sources-config";
import { WhatsappNumberConfig } from "./components/whatsapp-number-config";
import { WorkerSelectionModeToggle } from "./components/worker-selection-mode-toggle";
import { WorkerSelectionToggle } from "./components/worker-selection-toggle";
import { useOnlineBookingConfig } from "./hooks/use-online-booking-config";

export function OnlineSettingsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation("settings");
  const { form, isLoading, saving, error, save, discard, saveField, savingFields } =
    useOnlineBookingConfig();

  const values = useWatch({ control: form.control });

  return (
    <NavigationGuardProvider
      hasUnsavedChanges={form.formState.isDirty}
      onSave={save}
      onDiscard={discard}
      saving={saving}
      description={t("messages.unsavedChanges")}
    >
      <BaseNavigationBar
        title={t("sections.online.title")}
        subtitle={t("sections.online.description")}
        leadElement={
          isMobile ? <BackButton key="back-button" onBack={() => navigate(-1)} /> : undefined
        }
        trailingElements={[
          <SettingsSearchTrigger key="search-trigger" />,
          <FormActionButtons
            key="form-actions"
            isDirty={form.formState.isDirty}
            onSave={save}
            onDiscard={discard}
            saving={saving}
          />,
        ]}
      />

      <PageContent>
        <Form {...form}>
          <div className="space-y-6">
            <ErrorAlert error={error} />

            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : !error ? (
              <div className="space-y-6">
                {/* Master Toggle */}
                <div id="field-online-enabled">
                  <SettingCard
                    title={t("bookingSettings.onlineBooking")}
                    icon={<Globe className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
                  >
                    <OnlineBookingToggle
                      saveField={saveField}
                      saving={savingFields.has("enabled")}
                    />
                  </SettingCard>
                </div>

                {/* Client-facing WhatsApp contact for the storefront CTAs. */}
                <SettingCard
                  title="Customer WhatsApp"
                  icon={
                    <MessageCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  }
                >
                  <WhatsappNumberConfig />
                </SettingCard>

                {/* Booking Configuration - Only show when enabled */}
                {values?.enabled && (
                  <>
                    {/* Temporary pause — its own mutation, applied immediately */}
                    <SettingCard
                      title={t("pauseBookings.cardTitle")}
                      icon={<PauseCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
                    >
                      <PauseBookingConfig />
                    </SettingCard>

                    <div id="field-online-slots-per-hour">
                      <SettingCard
                        title={t("bookingSettings.bookingSlots")}
                        icon={<Settings className="h-5 w-5 text-green-600 dark:text-green-400" />}
                      >
                        <BookingSlotsConfig />
                      </SettingCard>
                    </div>

                    <SettingCard
                      title={t("bookingSettings.bookingTiming")}
                      icon={<Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
                    >
                      <div className="space-y-6">
                        <BookingTimingConfig saveField={saveField} savingFields={savingFields} />
                        <Separator />
                        <div id="field-online-advance-booking-days">
                          <AdvanceBookingConfig />
                        </div>
                      </div>
                    </SettingCard>

                    <SettingCard
                      title={t("bookingSettings.additionalSettings")}
                      icon={<Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />}
                    >
                      <div className="space-y-6">
                        <div id="field-online-worker-selection-enabled">
                          <WorkerSelectionToggle
                            saveField={saveField}
                            saving={savingFields.has("workerSelectionEnabled")}
                          />
                        </div>
                        <Separator />
                        <div id="field-online-show-service-duration">
                          <ShowServiceDurationToggle
                            saveField={saveField}
                            saving={savingFields.has("showServiceDuration")}
                          />
                        </div>
                        <Separator />
                        <div id="field-online-service-selection-mode">
                          <ServiceSelectionModeToggle
                            saveField={saveField}
                            saving={savingFields.has("serviceSelectionMode")}
                          />
                        </div>
                        <Separator />
                        <div id="field-online-worker-selection-mode">
                          <WorkerSelectionModeToggle
                            saveField={saveField}
                            saving={savingFields.has("workerSelectionMode")}
                          />
                        </div>
                        <Separator />
                        <div id="field-online-bank-transfer-enabled">
                          <BankTransferToggle
                            saveField={saveField}
                            saving={savingFields.has("bankTransferEnabled")}
                          />
                        </div>
                      </div>
                    </SettingCard>

                    <SettingCard
                      title={t("trafficSources.title")}
                      icon={<Settings className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />}
                    >
                      <div className="space-y-6">
                        <SalonSlugConfig />
                        <Separator />
                        <TrafficSourcesConfig />
                      </div>
                    </SettingCard>
                  </>
                )}
              </div>
            ) : null}
          </div>
        </Form>
      </PageContent>
    </NavigationGuardProvider>
  );
}
